import {
  useExportFeedbackViaEmail,
  useFetchRecordCount,
} from '@modules/common/hooks';
import { getAggregatePeriodFromDateRange } from '@modules/common/utils';
import {
  useGetQuantifyDuration,
  useGetQuantifyWhereClause,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getHasMetadataFilterForEqlField } from '@modules/quantify/utils';
import { useMemo } from 'react';
import { EQLProps } from 'v2/core/repository/eql/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { useGetExportFeedbackRecordsModal } from 'v2/views/components/ExportFeedbackRecordsModalContent/hooks';

interface UseExportQuantifyFeedbackOptions {
  view?: 'quantify' | 'dashboard';
}

export const useExportQuantifyFeedback = ({
  view = 'quantify',
}: UseExportQuantifyFeedbackOptions = {}) => {
  const query = useQuantifyStore(({ query }) => query);
  const whereClause = useGetQuantifyWhereClause();
  const duration = useGetQuantifyDuration();
  const metadataFilter = getHasMetadataFilterForEqlField(query.groupByField);

  const queryContainer = useMemo(() => {
    return {
      DateRange: duration as EQLProps['dateRange'],
      WhereClause: metadataFilter
        ? combineWhereClauses(whereClause, metadataFilter, OperationType.AND)
        : whereClause,
      AggregatePeriod: getAggregatePeriodFromDateRange(duration),
    } as QueryContainer;
  }, [duration, metadataFilter, whereClause]);

  const { data: feedbackCount } = useFetchRecordCount({ queryContainer });
  const [exportFeedbackViaEmail] = useExportFeedbackViaEmail({
    exportedFrom: view === 'quantify' ? 'Quantify Page' : 'Dashboard Page',
  });

  const exportFeedbackRecordModal = useGetExportFeedbackRecordsModal({
    exportFeedback: async ({ includeContent, includeOriginalContent }) => {
      await exportFeedbackViaEmail({
        queryContainer,
        includeContent,
        includeOriginalContent,
      });
    },
    feedbackCount,
  });

  return exportFeedbackRecordModal;
};
